import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Legend from 'components/molecules/Legend';
import Loader from 'components/molecules/Loader';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PVM_ANALYSIS_SUB_FACTOR_CLICKED,
    PVM_ANALYSIS_DOWNLOAD_DATA,
    PVM_ANALYSIS_COPY_SCREENSHOT,
} from 'utils/mixpanelEventNames';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { colorConstants } from 'config/colorConstants';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { graphXAxisLabelFontSize } from '../GrowthAnalysis/SalesAttributionDrilldownChart';
import PVMTrendChart1 from './PVMTrendChart1';
import PVMInfiniteImpactChartChartContainer, { legendData } from './PVMInfiniteImpactChartContainer';
import { useFetchFeatureData } from 'hooks/pvmAnalysis';
import { useFetchFeatureTimeSeries } from 'hooks/pvmAnalysis/useFetchFeatureTimeSeries';
import { PVMUrls } from 'config/urls';
import { FilterContextData } from 'contexts/FilterContext';
import { useGetCurrencyForTenant } from 'hooks/general/useCurrencyForClient';

export const height = Math.max(250, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200
export const graphItemContainerClass = 'p-4 mb-4 border border-borderLinePurpule rounded';
export const subHeaderClass = 'm-0 font-medium font-lg text-subHeaderColor';
export const yAxisLabelClass = 'text-greyFont font-xs font-medium';
const yAxisGraphWidth = calculateFontSize(window.innerWidth, 120);

type Props = {
    drillDownItem: any;
    commonVariables: any;
    level2DrillDownItem: any;
    setLevel2DrillDownItem: Function;
    exportTimePeriodHeader: any;
    decimalForNonPercentage: number;
    decimalForPercentage: number;
};

const commonYAxisStyle: any = {
    width: yAxisGraphWidth,
    minWidth: yAxisGraphWidth,
    overflowWrap: 'anywhere',
    fontSize: graphXAxisLabelFontSize,
};

const PVMAnalysisDrillDownContainer = ({
    drillDownItem,
    commonVariables: groupedCommonVariables,
    level2DrillDownItem,
    setLevel2DrillDownItem,
    exportTimePeriodHeader,
    decimalForNonPercentage,
    decimalForPercentage,
}: Props) => {
    const { attributeTypes, idForRestOfComponentLoading, allCheckedAttribute, timeSelectionLabel } =
        useContext(FilterContextData);
    const [level1DrillDownItem, setLevel1DrillDownItem] = useState<any>(null);
    const featureChartRef = useRef<any>();
    const featureTrendChart1Ref = useRef<any>();

    // check this function
    const containerWidth = useContainerWidth({
        containerId: '', //giving an incorrect id so that it takes from main container by default
        buffer: 16 * 3 + 2, //padding 16 * 3 + border 2
    });

    useEffect(() => {
        setLevel1DrillDownItem(null);
    }, [drillDownItem]);

    const memoisedLegends = useMemo(() => {
        const result: Array<any> = [];
        result.push({ label: drillDownItem.category, color: colorConstants.cyan });
        if (level1DrillDownItem) {
            result.push({ label: level1DrillDownItem.category, color: colorConstants.pink });
        }
        return result;
    }, [drillDownItem, level1DrillDownItem]);

    const chartWidth = containerWidth / 2 - 16 * 3; //subtract inner padding for 2 charts

    const commonVariables = useMemo(() => {
        return {
            ...groupedCommonVariables,
            feature_group_name: drillDownItem.apiParamKey,
        };
    }, [groupedCommonVariables, drillDownItem]);

    const {
        data: featureData,
        loading: featureDataLoading,
        error: featureDataError,
    } = useFetchFeatureData({
        queryKey: ['pvm-feature-data', commonVariables],
        variables: commonVariables,
        queryConfig: {
            // enabled: !idForRestOfComponentLoading && !!selectedMonth?.value,
        },
    });

    const {
        data: timeSeriesData,
        loading: timeSeriesDataLoading,
        error: timeSeriesDataError,
    } = useFetchFeatureTimeSeries({
        queryKey: ['pvm-timeseries-data', commonVariables],
        variables: commonVariables,
        queryConfig: {
            // enabled: !idForRestOfComponentLoading && !!selectedMonth?.value,
        },
        timeSeriesObjKey: drillDownItem.category,
        url: PVMUrls.GroupTimeSeriesUrl,
    });

    const {
        data: timeSeriesFeatureData,
        loading: timeSeriesFeatureDataLoading,
        error: timeSeriesFeatureDataError,
    } = useFetchFeatureTimeSeries({
        queryKey: [
            'pvm-timeseries-feature-data',
            { ...commonVariables, pvm_feature_name: level1DrillDownItem?.category },
        ],
        variables: { ...commonVariables, pvm_feature_name: level1DrillDownItem?.apiParamKey },
        queryConfig: {
            enabled: !!level1DrillDownItem?.apiParamKey,
        },
        timeSeriesObjKey: level1DrillDownItem?.category,
        url: PVMUrls.FeatureTimeSeriesUrl,
    });

    const currency = useGetCurrencyForTenant();
    let xAxisLabel: any = 'Sales ' + currency;

    const memoisedAttributeDropdownValues = useMemo(() => {
        const results: any = [];
        if (!idForRestOfComponentLoading) {
            Object.keys(attributeTypes).forEach((dim: any) => {
                const obj = attributeTypes[dim];

                obj?.forEach((k: string) => {
                    const dpValue: any = { dim };
                    dpValue.label = k.replace(/_/g, ' ');
                    dpValue.value = k.toLocaleLowerCase();
                    results.push(dpValue);
                });
            });
        }

        return results;
    }, [attributeTypes, idForRestOfComponentLoading]);

    //bar graph height
    const barHeight1 = Math.max(
        77,
        Array.isArray(featureData?.data) && featureData?.data?.length > 0 ? height / featureData?.data?.length : 0,
    );

    const memoisedTrendChartData = useMemo(() => {
        let result: any = {};
        if (timeSeriesData?.data) {
            result = _.clone(timeSeriesData);
        }
        // merging feature data
        if (timeSeriesFeatureData?.data && level1DrillDownItem?.category) {
            const newData = result.data?.map((item: any) => {
                return {
                    ...item,
                    [level1DrillDownItem.category]: timeSeriesFeatureData?.data?.find(
                        (k: any) => k.period === item.period,
                    )[level1DrillDownItem.category],
                };
            });
            result.minVal = Math.min(result.minVal, timeSeriesFeatureData.minVal);
            result.maxVal = Math.max(result.maxVal, timeSeriesFeatureData.maxVal);
            result.data = newData;
        }
        return result;
    }, [timeSeriesData, timeSeriesFeatureData, level1DrillDownItem]);

    const combineLoading = timeSeriesDataLoading || timeSeriesFeatureDataLoading;
    const combinedError = !!timeSeriesDataError; // TODO : add second chart error

    return (
        <section className="flex flex-col">
            <div className="flex gap-4">
                {/* CHART 1 CONTAINER */}
                <div className={`${graphItemContainerClass} mb-0 flex-1 overflow-hidden`}>
                    {/* ERROR */}
                    {featureDataError && !featureDataLoading ? (
                        <div
                            style={{
                                height: height + 50,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <ErrorBox msg={String(featureDataError)} />
                        </div>
                    ) : // LOADING
                    featureDataLoading ? (
                        <div style={{ height: height + 50 }}>
                            <Loader />
                        </div>
                    ) : (
                        <div ref={featureChartRef}>
                            <div className="flex justify-between items-center">
                                <p className={subHeaderClass}>{drillDownItem.category} </p>
                                <div className="flex items-center justify-end gap-2">
                                    <CopyToClipBoard
                                        mixpanelEvent={() =>
                                            trackEventMixpanel(PVM_ANALYSIS_COPY_SCREENSHOT, {
                                                PVMSubChart: `${drillDownItem.category}`,
                                            })
                                        }
                                        refOfComponent={featureChartRef}
                                    />
                                    <DownloadExcel
                                        customTimePeriodHeader={exportTimePeriodHeader}
                                        mixpanelEvent={() =>
                                            trackEventMixpanel(PVM_ANALYSIS_DOWNLOAD_DATA, {
                                                PVMSubChart: `${drillDownItem.category}`,
                                            })
                                        }
                                        title={drillDownItem?.category}
                                        excelData={[
                                            ...(featureData?.data?.map((item: any) => [item.category, item.sales]) ||
                                                []),
                                        ]}
                                        headers={[drillDownItem?.category, 'Sales']}
                                    />
                                </div>
                            </div>
                            <div
                                className="flex justify-center items-center"
                                style={{ marginTop: 14, marginBottom: 24 }}
                            >
                                <Legend data={legendData} />
                            </div>
                            <div style={{ height }} className=" overflow-auto">
                                {featureData?.data?.map((item: any, index: number) => {
                                    return (
                                        <div
                                            onClick={() => {
                                                trackEventMixpanel(PVM_ANALYSIS_SUB_FACTOR_CLICKED, {
                                                    PVMFactorName: commonVariables.feature_group_name,
                                                    PVMSubFactorName: item?.category,
                                                });

                                                setLevel1DrillDownItem(
                                                    level1DrillDownItem?.category === item?.category ? null : item,
                                                );
                                            }}
                                            style={{
                                                background:
                                                    level1DrillDownItem?.category === item?.category
                                                        ? colorConstants.grey
                                                        : 'none',
                                            }}
                                            className="relative flex items-center"
                                        >
                                            <div style={{ ...commonYAxisStyle }} className="text-center p-[2px]">
                                                {item?.category}
                                            </div>
                                            <Barchart
                                                offsetXDistance={-6}
                                                padding={[0, 10, 0, 10]}
                                                barWidth={30}
                                                dataset={[item]}
                                                height={barHeight1}
                                                loading={false}
                                                chartContainer={`attribution-drillDown-1-${index}`}
                                                fields={['category', 'sales']}
                                                intervalLabelTooltipKey={'labelSales'}
                                                minBound={scaleMinValue(50, featureData?.minVal)}
                                                maxBound={scaleMaxValue(50, featureData?.maxVal)}
                                                width={chartWidth - yAxisGraphWidth}
                                                transpose
                                                intervalFormatter={(val: any) => {
                                                    return formatNumWithKMBAlt(
                                                        val,
                                                        decimalForNonPercentage,
                                                        decimalForNonPercentage,
                                                    );
                                                }}
                                                tooltipFormatter={(xAxis, val) => {
                                                    return {
                                                        name: xAxis,
                                                        value: formatNumWithKMBAlt(
                                                            val,
                                                            decimalForNonPercentage,
                                                            decimalForNonPercentage,
                                                        ),
                                                    };
                                                }}
                                                Xline={false}
                                                colorKey={'color'}
                                                intervalStyle={{ cursor: 'pointer' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {/* X AXIS GRAPH COMPONENT */}
                            <Barchart
                                additionalStyle={{ marginLeft: yAxisGraphWidth - 10 }}
                                padding={[0, 10, 20, 10]}
                                dataset={[]}
                                height={50}
                                loading={false}
                                chartContainer={`attribution-drillDown-X-1-${1}`}
                                fields={['category', 'sales']}
                                intervalLabelTooltipKey={'labelSales'}
                                minBound={scaleMinValue(50, featureData?.minVal)}
                                maxBound={scaleMaxValue(50, featureData?.maxVal)}
                                width={chartWidth - yAxisGraphWidth + 20}
                                transpose
                                yAxisLabelFormatter={(val: any) => {
                                    return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                                }}
                                YGrid={false}
                                xAxisConfig={false}
                                colorKey={'color'}
                            />
                            <p className={`text-center relative ${yAxisLabelClass}`}>{xAxisLabel} </p>
                        </div>
                    )}
                </div>
                {/* CHART 2 CONTAINER  - TREND CHART */}
                <div className={`${graphItemContainerClass} mb-0 flex-1 overflow-hidden`}>
                    {/* ERROR */}
                    {!!timeSeriesDataError && !timeSeriesDataLoading ? (
                        <div
                            style={{
                                height: height + 50,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <ErrorBox msg={String(timeSeriesDataError || 'Failed to load data')} />
                        </div>
                    ) : // LOADING
                    combineLoading ? (
                        <div style={{ height: height + 50 }}>
                            <Loader />
                        </div>
                    ) : (
                        <div className={` mb-0 flex-1 overflow-hidden`} ref={featureTrendChart1Ref}>
                            <div className="flex justify-between items-center">
                                <p className={subHeaderClass}>{drillDownItem.category} Trend </p>
                                <div className="flex items-center justify-end gap-2">
                                    <CopyToClipBoard
                                        mixpanelEvent={() =>
                                            trackEventMixpanel(PVM_ANALYSIS_COPY_SCREENSHOT, {
                                                PVMTrendData: `${drillDownItem.category} Trend`,
                                            })
                                        }
                                        refOfComponent={featureTrendChart1Ref}
                                    />
                                    <DownloadExcel
                                        customTimePeriodHeader={exportTimePeriodHeader}
                                        mixpanelEvent={() =>
                                            trackEventMixpanel(PVM_ANALYSIS_DOWNLOAD_DATA, {
                                                PVMTrendData: `${drillDownItem.category} Trend`,
                                            })
                                        }
                                        title={`${drillDownItem.category} Trend`}
                                        excelData={[
                                            ...(memoisedTrendChartData?.data?.map((item: any) => [
                                                item.period,
                                                ...memoisedLegends
                                                    .map((i: any) => i.label)
                                                    .map((legendKey: string) => item[legendKey]),
                                            ]) || []),
                                        ]}
                                        headers={['Period', ...memoisedLegends.map((i: any) => i.label)]}
                                    />
                                </div>
                            </div>
                            <div
                                className="flex justify-between items-center"
                                style={{ marginTop: 14, marginBottom: 24 }}
                            >
                                <p className={`${yAxisLabelClass}`}>{xAxisLabel}</p>
                                <Legend data={memoisedLegends} />
                                <p></p>
                            </div>
                            <div className=" overflow-auto">
                                <PVMTrendChart1
                                    drillDownItem={drillDownItem}
                                    lineChartResult={memoisedTrendChartData}
                                    overflowWidth={chartWidth - yAxisGraphWidth + 20}
                                    fields={memoisedLegends.map((i: any) => i.label)}
                                    lineChartColors={[colorConstants.cyan, colorConstants.pink]}
                                    combinedLoading={combineLoading}
                                    combinedError={combinedError}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Infinite attribute component */}
            <PVMInfiniteImpactChartChartContainer
                commonVariables={{
                    ...commonVariables,
                    pvm_feature_name: level1DrillDownItem?.apiParamKey || drillDownItem?.apiParamKey,
                }}
                exportTimePeriodHeader={exportTimePeriodHeader}
                attributeDropdownOptions={memoisedAttributeDropdownValues}
                level1DrillDownItem={level1DrillDownItem}
                level2DrillDownItem={level2DrillDownItem}
                setLevel2DrillDownItem={setLevel2DrillDownItem}
                drillDownItem={drillDownItem}
            />
        </section>
    );
};

export default PVMAnalysisDrillDownContainer;
